"use client";
/**
 * PrintDocument — prints the conversation (or a single message) in a clean,
 * printer-friendly document format.
 *
 * The document is portaled to <body> and the styles in globals.css hide the
 * rest of the app during printing (display:none on body siblings), so the
 * output is a proper multi-page document rather than a snapshot of the DOM.
 */
import { createPortal } from "react-dom";
import type { Message } from "@/modes/ask/types";
import { MarkdownRenderer } from "./MarkdownRenderer";

interface PrintDocumentProps {
  /** Conversation title shown in the document header */
  title: string;
  messages: Message[];
}

function MsgContent({ message }: { message: Message }) {
  if (message.role === "user") {
    return (
      <div
        className="pd-content pd-user-content"
        // Content is plain text with <br> newlines (same storage as chat view)
        dangerouslySetInnerHTML={{ __html: message.content }}
      />
    );
  }

  if (message.type === "upgrade" || message.type === "session_completed") {
    return (
      <div className="pd-content pd-upgrade">
        {message.content.replace(/<[^>]*>/g, "").trim() ??
          "You've reached the token limit for this conversation."}
      </div>
    );
  }

  return (
    <div className="pd-content">
      <MarkdownRenderer content={message.content} isStreaming={false} forceLightTheme />
    </div>
  );
}

export function PrintDocument({ title, messages }: PrintDocumentProps) {
  return createPortal(
    <div className="print-document">
      <div className="pd-header">
        <div className="pd-title">SENSEI ASK</div>
        <div className="pd-conv-title">{title}</div>
        <div className="pd-meta">
          Generated on {new Date().toLocaleString([], { dateStyle: "long", timeStyle: "short" })}
        </div>
      </div>

      {messages.map((msg, i) => (
        <div
          key={`${msg.id ?? i}-${i}`}
          className={`pd-msg ${msg.role === "user" ? "pd-user" : "pd-assistant"}`}
        >
          <div className={`pd-author ${msg.role === "user" ? "pd-author-user" : ""}`}>
            {msg.role === "user" ? "You" : "sensei."}
          </div>
          <MsgContent message={msg} />
          {msg.token_usage?.total_tokens != null && msg.role === "assistant" && (
            <div className="pd-tokens">Tokens: {msg.token_usage.total_tokens.toLocaleString()}</div>
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}